import {
  TEMPLATE_SCHEMA_VERSION,
  isQuestionScored,
  type Condition,
  type ConditionRule,
  type Question,
  type TemplateDefinition,
} from './template';

/**
 * Structural validation for template definitions. Used on the seed file (in
 * tests) and at publish time (Template Builder, Phase 5): a template with
 * errors must not be published (spec §5). Returns human-readable errors;
 * empty array = valid.
 */

export interface TemplateSettings {
  /** 'none' = a form with no score (e.g. the DM Visit report — owner rulings 2026-10-20). */
  scoringMode: 'none' | 'simple' | 'weighted' | 'weighted_critical';
  criticalBehavior?: 'auto_fail' | 'cap';
  criticalCap?: number;
}

const ID_PATTERN = /^[a-z0-9][a-z0-9_-]*$/;

/**
 * Answers live in plain objects keyed by question/field id, so an id that
 * is a built-in object property ("constructor", "valueof"…) would read as
 * already answered. Only hand-edited JSON can produce one (generated ids are
 * random), but publish must still refuse it.
 */
const RESERVED_IDS = new Set(Object.getOwnPropertyNames(Object.prototype).map((n) => n.toLowerCase()));
// '__notes' suffix: the form stores a section's notes under `${sectionId}__notes`.
const validId = (id: unknown) =>
  typeof id === 'string' && ID_PATTERN.test(id) && !RESERVED_IDS.has(id) && !id.endsWith('__notes');

/**
 * Upper bounds (5c): the scoring engine and the full-audit share math stay
 * finite and readable. Real templates use weights 1–10 and up to 10 entries.
 */
export const MAX_WEIGHT = 100;
/** A subsection title is a title, not a paragraph (owner ruling 2026-11-02: detail goes in More detail). */
export const SUBSECTION_TITLE_MAX = 120;
export const MAX_SAMPLE_COUNT = 50;

export function validateTemplate(
  def: TemplateDefinition,
  settings?: TemplateSettings,
): string[] {
  const errors: string[] = [];

  if (def.schemaVersion !== TEMPLATE_SCHEMA_VERSION) {
    errors.push(
      `schemaVersion must be ${TEMPLATE_SCHEMA_VERSION}, got ${def.schemaVersion}`,
    );
  }
  if (!def.name?.trim()) errors.push('name is required');
  if (!def.auditType?.trim()) errors.push('auditType is required');
  if (!def.sections?.length) {
    errors.push('template has no sections');
    return errors;
  }

  const sectionIds = new Set<string>();
  const questionsInOrder: Question[] = [];
  const questionIds = new Set<string>();

  for (const section of def.sections) {
    if (!validId(section.id)) {
      errors.push(`section id '${section.id}' is not a valid id`);
    }
    if (sectionIds.has(section.id)) {
      errors.push(`duplicate section id '${section.id}'`);
    }
    sectionIds.add(section.id);
    if (!section.title?.trim()) {
      errors.push(`section '${section.id}' has no title`);
    }
    if (!section.questions?.length) {
      errors.push(`section '${section.id}' has no questions`);
      continue;
    }

    for (const q of section.questions) {
      if (!validId(q.id)) {
        errors.push(`question id '${q.id}' is not a valid id`);
      }
      if (questionIds.has(q.id)) {
        errors.push(`duplicate question id '${q.id}'`);
      }
      questionIds.add(q.id);
      if (!q.text?.trim()) errors.push(`question '${q.id}' has no text`);
      if (q.subsection !== undefined && (typeof q.subsection !== 'string' || q.subsection.trim().length > SUBSECTION_TITLE_MAX)) {
        errors.push(`question '${q.id}' subsection title must be ${SUBSECTION_TITLE_MAX} characters or fewer`);
      }
      // typeof: a hand-edited "5" (string) passes > and <= by coercion,
      // then scoring concatenates it instead of adding.
      if (q.weight !== undefined && (typeof q.weight !== 'number' || !(q.weight > 0))) {
        errors.push(`question '${q.id}' weight must be > 0`);
      } else if (q.weight !== undefined && !(q.weight <= MAX_WEIGHT)) {
        errors.push(`question '${q.id}' weight must be ${MAX_WEIGHT} or less`);
      }
      // A sample group keeps its flag while no field counts (the editor hides
      // the checkbox then, and scoring ignores it) — not a publish blocker.
      if (q.critical && !isQuestionScored(q) && q.type !== 'sample_group') {
        errors.push(`question '${q.id}' is critical but not scored`);
      }
      // The scoring engine only scores yes/no answers (scoring.ts).
      if (q.scored && q.type !== 'yes_no' && q.type !== 'yes_no_na' && q.type !== 'sample_group') {
        errors.push(`question '${q.id}' is marked scored, but only Yes/No questions can be scored`);
      }

      if (q.type === 'multi_select') {
        if (!q.options?.length) {
          errors.push(`multi_select '${q.id}' has no options`);
        } else if (q.options.some((o) => !o.trim())) {
          errors.push(`multi_select '${q.id}' has a blank choice`);
        } else if (new Set(q.options.map((o) => o.trim().toLowerCase())).size !== q.options.length) {
          errors.push(`multi_select '${q.id}' has duplicate options`);
        }
      } else if (q.options) {
        errors.push(`question '${q.id}' has options but is not multi_select`);
      }

      if (q.type === 'sample_group') {
        if (!q.sampleCount || q.sampleCount < 1) {
          errors.push(`sample_group '${q.id}' needs sampleCount >= 1`);
        } else if (!Number.isInteger(q.sampleCount) || q.sampleCount > MAX_SAMPLE_COUNT) {
          errors.push(`sample_group '${q.id}' sampleCount must be a whole number up to ${MAX_SAMPLE_COUNT}`);
        }
        if (
          q.minCount !== undefined &&
          (!Number.isInteger(q.minCount) || q.minCount < 0 || q.minCount > (q.sampleCount ?? 0))
        ) {
          errors.push(
            `sample_group '${q.id}' minCount must be a whole number between 0 and sampleCount`,
          );
        }
        if (!q.fields?.length) {
          errors.push(`sample_group '${q.id}' has no fields`);
        } else {
          const fieldIds = new Set<string>();
          for (const f of q.fields) {
            if (!validId(f.id)) {
              errors.push(`sample field id '${q.id}.${f.id}' is not a valid id`);
            }
            if (fieldIds.has(f.id)) {
              errors.push(`sample_group '${q.id}' has duplicate field id '${f.id}'`);
            }
            fieldIds.add(f.id);
            if (!f.label?.trim()) {
              errors.push(`sample field '${q.id}.${f.id}' has no label`);
            }
            if (f.scored && f.type !== 'yes_no' && f.type !== 'yes_no_na') {
              errors.push(`sample field '${q.id}.${f.id}' is marked scored, but only Yes/No fields can be scored`);
            }
          }
          for (const f of q.fields) {
            if (f.requiredIf) {
              const target = q.fields.find((x) => x.id === f.requiredIf!.fieldId);
              if (!target) {
                errors.push(
                  `sample field '${q.id}.${f.id}' requiredIf references unknown field '${f.requiredIf.fieldId}'`,
                );
              } else if (target.id === f.id) {
                errors.push(`sample field '${q.id}.${f.id}' requiredIf can’t depend on itself`);
              } else if (target.type !== 'yes_no' && target.type !== 'yes_no_na') {
                errors.push(
                  `sample field '${q.id}.${f.id}' requiredIf target '${q.id}.${target.id}' must be yes_no or yes_no_na`,
                );
              } else if (!['yes', 'no', 'na'].includes(f.requiredIf.equals) || (f.requiredIf.equals === 'na' && target.type !== 'yes_no_na')) {
                errors.push(
                  `sample field '${q.id}.${f.id}' requiredIf answer '${f.requiredIf.equals}' isn’t possible for field '${q.id}.${target.id}'`,
                );
              } else if (target.requiredIf) {
                // Two fields "required when" each other (or a chain of them)
                // would let a completely blank entry count toward minCount.
                errors.push(
                  `sample field '${q.id}.${f.id}' requiredIf target '${q.id}.${target.id}' must itself always be required`,
                );
              }
            }
          }
        }
      } else {
        if (
          q.sampleCount !== undefined ||
          q.fields !== undefined ||
          q.minCount !== undefined
        ) {
          errors.push(`question '${q.id}' has sample_group config but is type ${q.type}`);
        }
      }

      if (q.budget !== undefined && (typeof q.budget !== 'boolean' || (q.budget && q.type !== 'number' && q.type !== 'percent'))) {
        errors.push(`question '${q.id}' is marked as a budget / goal question but is not a number or percent question`);
      }

      if (q.prefillStoreAttribute && q.type !== 'yes_no') {
        errors.push(
          `question '${q.id}' has prefillStoreAttribute but is not a yes_no gate`,
        );
      }

      questionsInOrder.push(q);
    }
  }

  // Conditionals: gate must exist, be yes_no, and be declared BEFORE the
  // gated element (prevents cycles and forward references the renderer
  // can't resolve top-to-bottom). And the gate must be on screen whenever
  // the gated element could be: a hidden gate keeps its old answer, which
  // would keep revealing (and scoring, and requiring) what it gates.
  const declaredSoFar = new Set<string>();
  const sectionOf = new Map<string, number>();
  def.sections.forEach((sec, i) => sec.questions?.forEach((q) => sectionOf.set(q.id, i)));
  const rules: unknown[] = def.conditions === undefined ? [] : Array.isArray(def.conditions) ? def.conditions : [];
  if (def.conditions !== undefined && !Array.isArray(def.conditions)) errors.push('conditions must be a list');
  // Shown only sometimes: a legacy visibleIf, or the target of a Show / Hide rule.
  const sometimes = new Set<string>();
  def.sections.forEach((sec) => {
    if (sec.visibleIf) sometimes.add(sec.id);
    sec.questions?.forEach((q) => q.visibleIf && sometimes.add(q.id));
  });
  for (const r of rules as Partial<ConditionRule>[]) {
    if ((r?.action === 'show' || r?.action === 'hide') && Array.isArray(r.targets)) {
      for (const t of r.targets) if (typeof t === 'string') sometimes.add(t);
    }
  }
  const gateIsValid = (cond: Condition, owner: string, ownerSection: number, ownerIsSection: boolean) => {
    const gate = questionsInOrder.find((q) => q.id === cond.questionId);
    if (!gate) {
      errors.push(`${owner} visibleIf references unknown question '${cond.questionId}'`);
      return;
    }
    const gateSection = sectionOf.get(gate.id)!;
    const gateSectionId = def.sections[gateSection]?.id ?? '';
    if (sometimes.has(gate.id) || (sometimes.has(gateSectionId) && (ownerIsSection || gateSection !== ownerSection))) {
      errors.push(
        `${owner} visibleIf gate '${cond.questionId}' is itself shown only sometimes — pick a question that is always shown`,
      );
    }
    if (cond.equals !== 'yes' && cond.equals !== 'no') {
      errors.push(`${owner} visibleIf answer must be yes or no`);
    }
    if (gate.type !== 'yes_no') {
      errors.push(
        `${owner} visibleIf gate '${cond.questionId}' must be yes_no (got ${gate.type})`,
      );
    }
    if (!declaredSoFar.has(cond.questionId)) {
      errors.push(
        `${owner} visibleIf gate '${cond.questionId}' must be declared earlier in the template`,
      );
    }
  };

  def.sections.forEach((section, si) => {
    if (section.visibleIf) gateIsValid(section.visibleIf, `section '${section.id}'`, si, true);
    for (const q of section.questions ?? []) {
      if (q.visibleIf) gateIsValid(q.visibleIf, `question '${q.id}'`, si, false);
      declaredSoFar.add(q.id);
    }
  });

  validateConditions(def, rules, questionsInOrder, sectionOf, sometimes, errors);

  // Scoring sanity (publish-time): a scoring mode needs something to score.
  if (settings?.scoringMode === 'none') {
    // A no-score form: nothing may count toward a score (scoring, weights
    // and failure summaries then all come out empty by construction).
    for (const q of questionsInOrder) {
      if (isQuestionScored(q)) {
        errors.push(`question '${q.id}' counts toward the score, but this template has no score`);
      }
    }
    if (settings.criticalBehavior) errors.push(`criticalBehavior only applies to weighted_critical mode`);
  } else if (settings) {
    const anyScored = questionsInOrder.some((q) => isQuestionScored(q));
    if (!anyScored) {
      errors.push(`scoringMode '${settings.scoringMode}' set but no question is scored`);
    }
    if (settings.scoringMode === 'weighted_critical') {
      if (!settings.criticalBehavior) {
        errors.push('weighted_critical requires criticalBehavior');
      }
      if (settings.criticalBehavior === 'cap') {
        const cap = settings.criticalCap;
        if (cap === undefined || cap < 0 || cap > 100) {
          errors.push('criticalBehavior cap requires criticalCap in 0..100');
        }
      }
    } else if (settings.criticalBehavior) {
      errors.push(`criticalBehavior only applies to weighted_critical mode`);
    }
  }

  return errors;
}

const RULE_ACTIONS = new Set(['show', 'hide', 'require', 'not_require']);

/**
 * The Conditions page's rules (owner rulings 2026-11-02): one trigger — a
 * Yes/No, Yes/No/N/A or multi-select question that is ALWAYS shown (no
 * chained gates: a hidden trigger keeps its old answer) and comes BEFORE
 * every target — and at least one target. Require / Not require take
 * questions only. Messages start with "condition '<id>'" so the builder pins
 * them to the rule.
 */
function validateConditions(
  def: TemplateDefinition,
  rules: readonly unknown[],
  questionsInOrder: readonly Question[],
  sectionOf: ReadonlyMap<string, number>,
  sometimes: ReadonlySet<string>,
  errors: string[],
): void {
  const order = new Map(questionsInOrder.map((q, i) => [q.id, i]));
  const byId = new Map(questionsInOrder.map((q) => [q.id, q]));
  const sectionIndex = new Map(def.sections.map((sec, i) => [sec.id, i]));
  const seen = new Set<string>([...order.keys(), ...sectionIndex.keys()]);
  rules.forEach((raw, i) => {
    const r = (raw && typeof raw === 'object' ? raw : {}) as Partial<ConditionRule>;
    const okId = validId(r.id);
    const who = okId ? `condition '${r.id}'` : `condition ${i + 1}`;
    if (!okId) errors.push(`condition ${i + 1} has an invalid id`);
    else if (seen.has(r.id!)) errors.push(`${who} has the same id as another item`);
    else seen.add(r.id!);
    if (!RULE_ACTIONS.has(r.action as string)) errors.push(`${who} has an unknown rule type`);

    const w = (r.when && typeof r.when === 'object' ? r.when : {}) as Partial<ConditionRule['when']>;
    const trigger = typeof w.questionId === 'string' ? byId.get(w.questionId) : undefined;
    if (!w.questionId) errors.push(`${who} has no question to check — pick the question after “when”`);
    else if (!trigger) errors.push(`${who} depends on unknown question '${w.questionId}'`);
    else if (trigger.type === 'yes_no' || trigger.type === 'yes_no_na') {
      const values = trigger.type === 'yes_no' ? ['yes', 'no'] : ['yes', 'no', 'na'];
      if (w.op !== 'is' || typeof w.value !== 'string' || !values.includes(w.value)) {
        errors.push(`${who} needs the answer to check (${trigger.type === 'yes_no' ? 'Yes or No' : 'Yes, No or N/A'})`);
      }
    } else if (trigger.type === 'multi_select') {
      if (w.op !== 'includes' && w.op !== 'not_includes') errors.push(`${who} needs "includes" or "does not include"`);
      if (typeof w.value !== 'string' || !(trigger.options ?? []).includes(w.value)) {
        errors.push(`${who} needs one of question '${trigger.id}' choices to check`);
      }
    } else {
      errors.push(`${who}: question '${trigger.id}' can’t be a trigger — only Yes/No, Yes/No/N/A and multiple-choice questions can`);
    }

    const targets = Array.isArray(r.targets) ? r.targets.filter((t): t is string => typeof t === 'string') : [];
    // (No trigger yet: that message is enough — the builder says "pick the trigger first".)
    if (targets.length === 0 && w.questionId) {
      errors.push(`${who} has nothing to apply to yet — add a question${r.action === 'require' || r.action === 'not_require' ? '' : ' or section'}`);
    }
    const triggerSection = trigger ? sectionOf.get(trigger.id) : undefined;
    let outsideTriggerSection = false;
    for (const t of new Set(targets)) {
      const isQuestion = order.has(t);
      const isSection = sectionIndex.has(t);
      if (!isQuestion && !isSection) {
        errors.push(`${who} applies to an item that no longer exists ('${t}')`);
        continue;
      }
      if (isSection && (r.action === 'require' || r.action === 'not_require')) {
        errors.push(`${who}: Require and Not require apply to questions, not to section '${t}'`);
      }
      if (isSection || sectionOf.get(t) !== triggerSection) outsideTriggerSection = true;
      if (!trigger) continue;
      if (t === trigger.id) errors.push(`${who} can’t apply to its own trigger`);
      else if (isQuestion && order.get(t)! < order.get(trigger.id)!) {
        errors.push(`${who} applies to question '${t}', which comes before its trigger — the trigger must come first`);
      } else if (isSection && sectionIndex.get(t)! <= triggerSection!) {
        errors.push(`${who} applies to section '${t}', which doesn’t come after its trigger’s section — the trigger must come first`);
      }
    }
    if (trigger) {
      const triggerSectionId = def.sections[triggerSection!]?.id ?? '';
      if (sometimes.has(trigger.id) || (sometimes.has(triggerSectionId) && outsideTriggerSection)) {
        errors.push(`${who} depends on question '${trigger.id}', which is itself shown only sometimes — pick a question that is always shown`);
      }
    }
  });
}
